// src/pages/VeryShortans.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Shortans.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter';
const API_BASE = 'https://digital.tifleducation.com/admin/public/api';

// Include all types to keep localStorage structure consistent
const ALL_TYPES = [
  'mcq',
  'fill_in_the_blanks',
  'true_false',
  'very_short_qa',
  'short_qa',
  'long_qa',
  'matching_type',
  'diagram_image_based'
];

const VeryShortans = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [veryShortLimit, setVeryShortLimit] = useState(0);

  // -------------------- FETCH DATA --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('User not logged in.');
          return;
        }

        // Fetch user info
        const dashRes = await axios.get(`${API_BASE}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (dashRes.data?.user) {
          setUser({
            id: dashRes.data.user.id,
            name: dashRes.data.user.name || '',
            email: dashRes.data.user.email || '',
            password: '',
          });
        }

        // Get selected Board / Grade / Subject
      const getStoredId = (rawKey) => {
          const raw = localStorage.getItem(rawKey);
          if (!raw) return null;
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && parsed.id != null) return parsed.id;
            // If parsed is a number/string ID, return that
            return parsed;
          } catch {
            // not JSON, return raw string (likely an id string)
            return raw;
          }
        };
        // Get selected board/grade/subject
        const boardId = getStoredId('selectedBoardId') || getStoredId('selectedBoard') || getStoredId('board_id') || getStoredId('board');
        const gradeId = getStoredId('selectedGradeId') || getStoredId('selectedGrade') || getStoredId('grade_id') || getStoredId('grade');
        const subjectId = getStoredId('selectedSubjectId') || getStoredId('selectedSubject') || getStoredId('subject_id') || getStoredId('subject');

        if (!boardId || !gradeId || !subjectId) {
          toast.error('Missing selected board/grade/subject.');
          return;
        }

        // Restore limit from regularPaperForm
        const regularForm = JSON.parse(localStorage.getItem('regularPaperForm') || '{}');
        let limit = Number(regularForm.very_short_qa);
        if (!limit || limit <= 0) limit = 5; // fallback default
        setVeryShortLimit(limit);

        // Fetch questions
        const res = await axios.post(
          `${API_BASE}/papers/fetch-questions`,
          { board: boardId, grade: gradeId, subject: subjectId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Collect Very Short Questions
        const veryShortQs = [];
        Object.entries(res.data || {}).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          const arr = types['Very Short Question Answer'] || [];
          (Array.isArray(arr) ? arr : []).forEach(q => {
            veryShortQs.push({ ...q, chapter_id: q.chapter_id ?? Number(chapterId) });
          });
        });
        setQuestions(veryShortQs);

        // Restore selected questions from localStorage
        const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
        const selectedMap = {};
        Object.values(saved).forEach(chapterData => {
          const arr = chapterData.very_short_qa || [];
          arr.forEach(id => { selectedMap[Number(id)] = true; });
        });
        setSelectedQuestions(selectedMap);

      } catch (error) {
        console.error("Error fetching data:", error.response?.data || error);
        toast.error('Failed to fetch very short questions.');
      }
    };

    fetchData();
  }, []);

  // -------------------- SELECT QUESTIONS --------------------
  const handleSelectQuestion = async (qId, chapterId) => {
    const alreadySelected = !!selectedQuestions[qId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!alreadySelected && !veryShortLimit) {
      toast.error('No very short ans limit set. Please set question counts first.');
      return;
    }
    if (!alreadySelected && currentCount >= veryShortLimit) {
      toast.error(`You can only select up to ${veryShortLimit} very short questions.`);
      return;
    }

    // Update state
    const nextSelected = { ...selectedQuestions };
    if (nextSelected[qId]) delete nextSelected[qId];
    else nextSelected[qId] = true;
    setSelectedQuestions(nextSelected);

    try {
      // Save in a normalized structure for Regularpaper
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
      const chapterKey = String(chapterId);
      if (!saved[chapterKey]) saved[chapterKey] = {};

      // Ensure all types exist
      ALL_TYPES.forEach(key => {
        if (!Array.isArray(saved[chapterKey][key])) saved[chapterKey][key] = [];
      });

      // Now update very_short_qa
      const arr = saved[chapterKey].very_short_qa;
      const idx = arr.findIndex(x => Number(x) === Number(qId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(qId));
      saved[chapterKey].very_short_qa = arr;

      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/papers/save-questions`,
        { chapter_id: Number(chapterId), type: "Very Short Question Answer", selected_questions: arr },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Selection saved');
    } catch (error) {
      console.error('Error saving very short selection:', error.response?.data || error);
      toast.error('Failed to save selection');
    }
  };

  // -------------------- PROFILE SAVE --------------------
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // -------------------- OUTSIDE CLICK --------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsModalOpen(false);
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // -------------------- RENDER --------------------
  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />
      <main className="main-content">
        <div className="short-header">
          <div className="short-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="short-welcome-text">Very Short Questions</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <FaUserCircle className="icon" onClick={() => setIsModalOpen(true)} />
          </div>
        </div>

        <div className="short-questions-container">
          {questions.length > 0 ? (
            questions.map((q, index) => (
              <div key={q.id} className="short-question-box">
                <div className="short-question">
                  <input
                    type="checkbox"
                    className="short-checkbox"
                    checked={!!selectedQuestions[q.id]}
                    onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                    disabled={!selectedQuestions[q.id] && Object.keys(selectedQuestions).length >= veryShortLimit}
                  />
                  <span className="question-text">
                    {index + 1}. {String(q.question || q.question_title || "").replace(/<\/?[^>]+(>|$)/g, "")}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Very Short Questions Found</h3>
              <p style={{ color: '#666' }}>
                There are currently no very short questions for this Board / Grade / Subject.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={(e) => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={(e) => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default VeryShortans;
